// Bootstrap. Không còn render gì của dashboard — toàn bộ state + markup nằm
// trong src/App.tsx. File này chỉ lo 4 việc:
//   1. quyết demo mode hay live mode
//   2. màn landing + MSAL sign-in (live)
//   3. nạp data lần đầu, dựng các adapter (reload / rto / entry api)
//   4. mount <App> vào #app-root
//
// Không còn innerHTML nào trong dự án — mọi UI là component React.
import { createElement } from "react";
import { createRoot } from "react-dom/client";
import { CONFIG } from "./config.js";
import { DEMO_FUNNEL, DEMO_ACHIEVEMENTS } from "./data-demo.js";
import { App } from "./App.tsx";

const $ = (sel) => document.querySelector(sel);

// Loại data test / người ngoài team TA khỏi mọi thống kê.
const cleanRows = (rows) =>
  rows.filter((r) => !(CONFIG.excludeRecruiters || []).includes(r.recruiter));
const cleanAchievements = (list) =>
  list.filter((a) => !(CONFIG.excludeRecruiters || []).includes(a.recruiter));

function mount(props) {
  createRoot($("#app-root")).render(createElement(App, props));
}

/* ---------- demo mode ---------- */

function bootDemo() {
  $("#demo-banner").hidden = false;

  // Store in-memory: form nhập liệu ghi vào đây, reload() đọc lại từ đây.
  const store = {
    rows: cleanRows(DEMO_FUNNEL),
    achievements: cleanAchievements(DEMO_ACHIEVEMENTS),
    stacks: ["Java", "Backend Web", "BA", "DevOps", "QA"],
    // Cùng shape với getRtoEntries() của graph.ts.
    rto: [
      { id: 1, name: "Nguyen Van A", position: "Java", level: "M1", eng: "6.0", recruiter: "AnhTD", status: "Ready", rtoDate: "2026-07-20", profileLink: "" },
      { id: 2, name: "Tran Thi B", position: "Java", level: "M2", eng: "6.5", recruiter: "MyLTP", status: "Ready", rtoDate: "2026-07-22", profileLink: "" },
      { id: 3, name: "Le Van C", position: "BA", level: "M1", eng: "6.0", recruiter: "LyPK", status: "Ready", rtoDate: "2026-07-25", profileLink: "" },
      { id: 4, name: "Pham Van D", position: "Backend Web", level: "M1+", eng: "7.0", recruiter: "DucPM", status: "Ready", rtoDate: "2026-07-26", profileLink: "" },
      { id: 5, name: "Hoang Thi E", position: "QA", level: "M1", eng: "5.5", recruiter: "AnhTD", status: "Ready", rtoDate: "2026-07-27", profileLink: "" },
      { id: 6, name: "Vu Van F", position: "QA", level: "M2", eng: "6.0", recruiter: "MyLTP", status: "Offered", rtoDate: "2026-07-15", profileLink: "" },
      { id: 7, name: "Dang Thi G", position: "MKT", level: "", eng: "6.0", recruiter: "VietBN", status: "Ready", rtoDate: "2026-07-24", profileLink: "" },
    ],
  };

  const users = [...new Set(store.rows.map((r) => r.recruiter))]
    .map((name, i) => ({ id: i + 1, name, email: i === 0 ? "demo" : name.toLowerCase() }));

  // API giả: ghi/xóa trong bộ nhớ để duyệt UI không cần SharePoint.
  const api = {
    addFunnelRow: async (f) => { store.rows.push({ ...f, recruiter: "Demo User" }); },
    addRtoEntry: async (f) => {
      store.rto.push({ ...f, id: Date.now(), recruiter: "Demo User", status: "Ready" });
    },
    addAchievement: async (f) => {
      store.achievements.push({
        month: f.month, kpiType: f.kpiType, title: f.title,
        recruiter: users.find((u) => u.id === f.recruiterId)?.name || "Demo User",
      });
    },
    deleteItem: async (list, id) => {
      if (list === CONFIG.funnelList) store.rows.splice(id, 1);
      else store.achievements.splice(id, 1);
    },
    getSiteUsers: async () => users,
    getRecentEntries: async () => ({
      funnel: store.rows.map((r, i) => ({
        Id: i, WeekEnding: r.weekEnding, Position: r.position,
        CandidatesContacted: r.contacted, CandidatesResponses: r.responses,
        Applications: r.applications, Interviews: r.interviews, Offers: r.offers,
        Hires: r.hires, Notes: r.notes || "", Author: { Title: r.recruiter },
        Created: r.weekEnding,
      })).reverse().slice(0, 100),
      kpi: store.achievements.map((a, i) => ({
        Id: i, KPIMonth: a.month + "-01", KPIType: a.kpiType, Title: a.title || "",
        Recruiter: { Title: a.recruiter }, Author: { Title: a.recruiter }, Created: a.month + "-01",
      })),
    }),
  };

  mount({
    initialRows: store.rows.slice(),
    initialAchievements: store.achievements.slice(),
    // slice(): App setState so sánh theo reference, trả lại đúng mảng cũ thì
    // React coi như không đổi và không vẽ lại.
    reload: async () => ({
      rows: store.rows.slice(),
      achievements: store.achievements.slice(),
    }),
    rto: {
      fetch: async () => store.rto.slice(),
      updateStatus: async (id, status) => {
        const it = store.rto.find((i) => i.id === id);
        if (it) it.status = status;
      },
      loadActiveStacks: async () => store.stacks.slice(),
      saveActiveStacks: async (arr) => { store.stacks = arr.slice(); },
    },
    entry: { isDemo: true, account: { name: "Demo User", username: "demo" }, api },
  });
}

/* ---------- live mode ---------- */

async function bootLive() {
  const graph = await import("./graph.ts");
  const { initAuth, signIn, getData, PermissionError } = graph;
  const landing = $("#landing");
  landing.hidden = false;

  const showError = (err) => {
    const msg = err instanceof PermissionError
      ? "Your account does not have access to the ORG HR Admin site. Please contact HR."
      : "Could not load data: " + err.message;
    const el = $("#landing-error");
    landing.hidden = false;
    // #app-root là container của React root — React quản children, không quản
    // attribute của chính nó, nên set hidden ở đây là an toàn.
    $("#app-root").hidden = true;
    el.textContent = msg;
    el.hidden = false;
  };

  const fetchData = async () => {
    const { rows, achievements } = await getData();
    return { rows: cleanRows(rows), achievements: cleanAchievements(achievements) };
  };

  let mounted = false;
  const loadDashboard = async () => {
    try {
      const data = await fetchData();
      landing.hidden = true;
      $("#app-root").hidden = false;
      $("#landing-error").hidden = true;
      if (mounted) return; // đã mount → App tự quản data qua reload()
      mounted = true;
      mount({
        initialRows: data.rows,
        initialAchievements: data.achievements,
        reload: async () => {
          try {
            const d = await fetchData();
            $("#landing-error").hidden = true;
            return d;
          } catch (err) {
            showError(err); // App bỏ qua rejection, state giữ nguyên data cũ
            throw err;
          }
        },
        rto: {
          fetch: graph.getRtoEntries,
          updateStatus: graph.updateRtoStatus,
          loadActiveStacks: graph.getActiveStacks,
          saveActiveStacks: graph.saveActiveStacks,
        },
        entry: { isDemo: false, account: graph.currentAccount(), api: graph },
      });
    } catch (err) {
      showError(err);
    }
  };

  $("#btn-signin").addEventListener("click", async () => {
    try {
      await signIn();
      await loadDashboard();
    } catch (err) {
      const el = $("#landing-error");
      el.textContent = "Sign-in failed: " + err.message;
      el.hidden = false;
    }
  });

  const account = await initAuth();
  if (account) await loadDashboard();
}

if (CONFIG.isDemo()) bootDemo();
else void bootLive();
